/**
 * Reads what somebody typed into a measurement box.
 *
 * `Number(raw)` was the whole of this, and it is wrong in the one way that
 * matters at a bench: "abc" becomes NaN, NaN becomes `null` in JSON, and the
 * API stored a 0 — a forced vital capacity of 0 L, a haemoglobin of 0 g/dL
 * flagged critical. The server now refuses that too; this is so the person
 * typing is told at the box, in words, before anything is sent.
 *
 * Only a plain decimal is a number here. "13,2" is refused rather than read
 * as 13.2 or 132, because which one was meant is exactly what cannot be
 * guessed, and "7,800" is as likely as either.
 */
const PLAIN_DECIMAL = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

export type Measured = { ok: true; value: number } | { ok: false; message: string };

export function parseMeasured(
  raw: string,
  opts: { unit?: string | null; plausible?: { low: number | null; high: number | null } | null } = {},
): Measured {
  const s = raw.trim();
  if (s === '') return { ok: false, message: 'Enter a value.' };
  if (!PLAIN_DECIMAL.test(s)) {
    return {
      ok: false,
      message: /,/.test(s)
        ? `“${s}” was not saved: use a dot for the decimal point and no commas (13.2, 7800).`
        : `“${s}” is not a number, so nothing was saved. Enter digits only, such as 13.2.`,
    };
  }
  const value = Number(s);
  if (!Number.isFinite(value)) return { ok: false, message: `“${s}” is not a usable number.` };

  const { low, high } = opts.plausible ?? { low: null, high: null };
  if ((low != null && value < low) || (high != null && value > high)) {
    const u = opts.unit ? ` ${opts.unit}` : '';
    const bounds = low != null && high != null ? `between ${low} and ${high}${u}`
      : low != null ? `at least ${low}${u}` : `at most ${high}${u}`;
    return {
      ok: false,
      message: `${value}${u} is not a value this test can produce (it reads ${bounds}). Check the decimal point and the unit.`,
    };
  }
  return { ok: true, value };
}
